import { apiFetch } from "@/client/lib/api";

import type {
  MaintenanceKind,
  MaintenanceStatus,
} from "@/client/features/maintenance/maintenance-api";

/** Staff-only client for authoring maintenance windows and sending notifications. */

export type MaintenanceAudience = "all" | "selected";
export type MaintenanceChannel = "slack" | "email";

export interface MaintenanceDeliveryView {
  channel: MaintenanceChannel;
  status: string;
  createdAt: string;
  target: string | null;
  error: string | null;
}

export interface StaffMaintenanceWindow {
  id: string;
  title: string;
  description: string;
  kind: MaintenanceKind;
  status: MaintenanceStatus;
  audience: MaintenanceAudience;
  startsAt: string;
  endsAt: string | null;
  publishedAt: string | null;
  emailOnPublish: boolean;
  createdAt: string;
  updatedAt: string;
  tenantTargets: { id: string; name: string }[];
  deliveries: MaintenanceDeliveryView[];
}

export interface CreateWindowInput {
  title: string;
  description: string;
  kind: MaintenanceKind;
  audience: MaintenanceAudience;
  startsAt: string;
  endsAt: string | null;
  emailOnPublish: boolean;
  tenantIds: string[];
  publish: boolean;
}

export type UpdateWindowInput = Partial<CreateWindowInput> & { status?: MaintenanceStatus };

export interface ChannelSendResult {
  channel: MaintenanceChannel;
  sent: number;
  skipped: number;
  failed: number;
  detail: { tenant: string; status: string; error?: string }[];
}

export const maintenanceAdminApi = {
  list: () =>
    apiFetch<{ windows: StaffMaintenanceWindow[]; slackConfigured: boolean }>("/staff/maintenance"),
  create: (input: CreateWindowInput) =>
    apiFetch<StaffMaintenanceWindow>("/staff/maintenance", { method: "POST", json: input }),
  update: (id: string, input: UpdateWindowInput) =>
    apiFetch<StaffMaintenanceWindow>(`/staff/maintenance/${encodeURIComponent(id)}`, {
      method: "PATCH",
      json: input,
    }),
  notify: (id: string, channel: MaintenanceChannel) =>
    apiFetch<ChannelSendResult>(`/staff/maintenance/${encodeURIComponent(id)}/notify`, {
      method: "POST",
      json: { channel },
    }),
  remove: (id: string) =>
    apiFetch<void>(`/staff/maintenance/${encodeURIComponent(id)}`, { method: "DELETE" }),
};
